import React, { useEffect, useRef, useState } from 'react'

import type {
  CMSComponentResponsiveImage,
  CMSProductPage,
} from '@/types/sanity'
import { $isStickyCTAVisible } from '@/stores/ui/stores'

import './StickyCTA.scss'
import { ResponsiveImage } from '../Shared/ResponsiveImage'
import { Icon } from '@/components/Icon'

type ProductStickyCTAProps = {
  activeVariantId: string
  product: CMSProductPage
  stickyCTA?: {
    showStickyCTA: boolean
    name: string
    subtitle?: string
    image: CMSComponentResponsiveImage
  }
}

export const ProductStickyCTA: React.FC<ProductStickyCTAProps> = ({
  activeVariantId,
  product,
  stickyCTA,
}) => {
  const [isIntersecting, setIntersecting] = useState(false)
  const [buttonText, setButtonText] = useState('')
  const visibleButtonRef = useRef<HTMLButtonElement | null>(null)
  const [activeVariantIdState, setActiveVariantIdState] =
    useState(activeVariantId)

  const activeVariant = product.variants.find(
    (variant) => Number(variant.id) === Number(activeVariantIdState),
  )
  const activeVariantInWaitlist = activeVariant?.waitlist
  // TODO: revisit this logic
  // const activeVariantAvailable =
  //   activeVariant?.active &&
  //   !activeVariant?.details?.optionDisabled &&
  //   variantIsAvailableForSale(activeVariant)

  useEffect(() => {
    const checkIntersection = () => {
      const buyingPanel = document.querySelector('.buying-panel__form')
      const header = document.querySelector('.header')

      if (buyingPanel && header) {
        const buyingPanelRect = buyingPanel.getBoundingClientRect()
        const headerRect = header.getBoundingClientRect()

        const isHeaderBelowBuyingPanel =
          headerRect.bottom > buyingPanelRect.bottom

        setIntersecting(isHeaderBelowBuyingPanel)
        document.body.classList.add('has-sticky')
      }
    }

    checkIntersection()
    window.addEventListener('scroll', checkIntersection)

    return () => {
      window.removeEventListener('scroll', checkIntersection)
    }
  }, [])

  const getVisibleButton = (buttons: NodeListOf<HTMLButtonElement>) => {
    buttons.forEach((button) => {
      if (button.style.display !== 'none') {
        visibleButtonRef.current = button
      }
    })
  }

  const determineButtonText = (
    variantInWaitlist = activeVariantInWaitlist,
    variant = activeVariant,
  ) => {
    if (variantInWaitlist) {
      return `JOIN THE WAITLIST`
    }
    return `${activeVariant?.sellingPlan ? 'subscribe' : 'add to bag'} | $${
      variant?.price || product.variants[0].price
    }`
  }

  useEffect(() => {
    const buttons = document.querySelectorAll(
      '.buying-panel-actions__action',
    ) as NodeListOf<HTMLButtonElement>
    getVisibleButton(buttons)
    const buttonText = determineButtonText()
    setButtonText(buttonText)
  }, [activeVariantIdState])

  useEffect(() => {
    const handleUpdateActiveVariant = (event: Event) => {
      const customEvent = event as CustomEvent<any>
      setActiveVariantIdState(customEvent.detail.id)
    }

    document.addEventListener(
      'buying-panel:update-active-variant',
      handleUpdateActiveVariant,
    )

    return () => {
      document.removeEventListener(
        'buying-panel:update-active-variant',
        handleUpdateActiveVariant,
      )
    }
  }, [])

  const handleClick = () => {
    visibleButtonRef.current?.click()
  }

  const isStickyVisible = isIntersecting && stickyCTA?.showStickyCTA

  useEffect(() => {
    $isStickyCTAVisible.set(!!isStickyVisible)
  }, [isStickyVisible])

  if (!stickyCTA?.showStickyCTA) return null

  return (
    <div
      className={`p-sticky-cta-pdp  ${isStickyVisible ? 'is-stuck' : ''}`}
      key={activeVariantId}
    >
      <div className="p-sticky-cta-pdp__info-container">
        <div className="p-sticky-cta-pdp__image-container">
          <ResponsiveImage
            document={stickyCTA.image}
            overrides={{
              desktopAspectRatio: '1/1',
              mobileAspectRatio: '1/1',
              loading: 'eager',
              customSizes: [1024],
            }}
          />
        </div>

        <div>
          <p className="type-label p-sticky-cta-pdp__title">{stickyCTA.name}</p>
          <p className="type-body p-sticky-cta-pdp__subtitle">
            {stickyCTA.subtitle}
          </p>
        </div>
      </div>
      <button
        onClick={handleClick}
        className="p-sticky-cta-pdp__action-button button button--primary"
      >
        <p>{buttonText}</p>
        <Icon name="primary-arrow-right" viewBox="0 0 21 11" />
      </button>
    </div>
  )
}
